import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  DollarSign, 
  ArrowUpRight, 
  Globe, 
  Mail, 
  ShieldCheck, 
  Plus, 
  Radio, 
  ChevronRight,
  TrendingUp,
  Smartphone,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const money = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ title, value, subtext, icon: Icon, color, trend }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="glass-card p-6 flex flex-col justify-between relative overflow-hidden group"
  >
    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/5 to-transparent rounded-bl-full pointer-events-none group-hover:scale-110 transition-transform duration-500" />
    
    <div className="flex justify-between items-start">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10 border border-white/5 shadow-lg`}>
        <Icon className={color.replace('bg-', 'text-')} size={24} />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-lg border border-emerald-500/20">
          <ArrowUpRight size={12} /> {trend}
        </span>
      )}
    </div>
    
    <div className="mt-4">
      <p className="text-slate-500 text-[10px] font-mono font-bold tracking-widest uppercase mb-1">{title}</p>
      <h3 className="admin-heading text-3xl font-black text-white font-heading tracking-tight">{value}</h3>
      {subtext && <p className="text-xs text-slate-400 font-mono mt-1">{subtext}</p>}
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_earnings: 0,
    today_earnings: 0,
    yesterday_earnings: 0,
    this_month_earnings: 0,
    last_month_earnings: 0,
  });
  const [appsCount, setAppsCount] = useState(0);
  const [activeChartTab, setActiveChartTab] = useState("volume");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const [statsRes, appsRes] = await Promise.all([
          axios.get(`${apiUrl}/api/stats`).catch(() => ({ data: {} })),
          axios.get(`${apiUrl}/api/apps`).catch(() => ({ data: [] })),
        ]);

        if (statsRes.data) {
          setStats({
            total_earnings: Number(statsRes.data.total_earnings) || 0,
            today_earnings: Number(statsRes.data.today_earnings) || 0,
            yesterday_earnings: Number(statsRes.data.yesterday_earnings) || 0,
            this_month_earnings: Number(statsRes.data.this_month_earnings) || 0,
            last_month_earnings: Number(statsRes.data.last_month_earnings) || 0,
          });
        }

        const rawApps = Array.isArray(appsRes.data) ? appsRes.data : appsRes.data?.data || [];
        setAppsCount(rawApps.length);
      } catch (err) {
        console.error('Failed to fetch dashboard telemetry:', err);
      }
    };
    fetchDashboardData();
  }, []);

  return (
    <div className="space-y-10 animate-fade-in font-sans">
      
      {/* CEO Welcome Banner & System Status Header */}
      <div className="admin-hero-panel relative overflow-hidden flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-mono mb-3">
            <Radio className="w-3.5 h-3.5 animate-pulse text-indigo-400" />
            <span>Quivix Digital Central Command</span>
          </div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-black text-white tracking-tight">
            Welcome Back, <span className="text-gradient">CEO Salman Khan</span>
          </h1>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            Real-time management dashboard for QuivixDigital operations, ecosystem applications, and revenue telemetry.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-mono text-slate-300">
            <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-blue-400" /> Dual HQs: New Mexico, USA & Jeddah, KSA</span>
            <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-400" /> System SLA: 99.99%</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 relative z-10">
          <Link
            to="/admin/apps"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all active:scale-95"
          >
            <Plus size={16} /> Add Product
          </Link>
          <Link
            to="/admin/push-email"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium text-xs transition-all active:scale-95"
          >
            <Mail size={16} className="text-blue-400" /> Broadcast Email
          </Link>
        </div>
      </div>

      {/* Real Live Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue Raised"
          value={`$${money(stats.total_earnings)}`}
          subtext="Live System Revenue"
          icon={DollarSign}
          color="bg-emerald-400"
          trend="Real Stats"
        />
        <StatCard
          title="Today's Velocity"
          value={`$${money(stats.today_earnings)}`}
          subtext="Daily Earnings"
          icon={TrendingUp}
          color="bg-blue-400"
          trend="Today"
        />
        <StatCard
          title="This Month Delta"
          value={`$${money(stats.this_month_earnings)}`}
          subtext="Monthly Volume"
          icon={Activity}
          color="bg-purple-400"
          trend="Monthly"
        />
        <StatCard
          title="Live Published Apps"
          value={`${appsCount} Apps`}
          subtext="Database Applications"
          icon={Smartphone}
          color="bg-orange-400"
          trend="Active"
        />
      </div>

      {/* Main Telemetry Visualizer & System Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* SVG Telemetry Visualization */}
        <div className="lg:col-span-2 glass-card p-8 min-h-[460px] flex flex-col justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-indigo-400">Live Analytics</span>
              <h2 className="admin-heading text-xl font-bold text-white mt-0.5">Earnings & Performance Curve</h2>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => setActiveChartTab("volume")}
                className={`px-3 py-1.5 text-[10px] font-mono font-bold rounded-lg border transition-all ${
                  activeChartTab === "volume" 
                    ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300" 
                    : "bg-white/5 border-white/5 text-slate-400 hover:text-white"
                }`}
              >
                Revenue (${money(stats.total_earnings)})
              </button>
              <button 
                onClick={() => setActiveChartTab("monthly")}
                className={`px-3 py-1.5 text-[10px] font-mono font-bold rounded-lg border transition-all ${
                  activeChartTab === "monthly" 
                    ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300" 
                    : "bg-white/5 border-white/5 text-slate-400 hover:text-white"
                }`}
              >
                Monthly (${money(stats.this_month_earnings)})
              </button>
            </div>
          </div>

          {/* SVG Telemetry Chart Area */}
          <div className="flex-1 bg-black/40 border border-white/10 rounded-2xl p-6 flex flex-col justify-between relative overflow-hidden">
            {/* Chart Grid Lines */}
            <div className="absolute inset-0 flex flex-col justify-between p-6 pointer-events-none opacity-20">
              <div className="border-b border-dashed border-white/20 w-full" />
              <div className="border-b border-dashed border-white/20 w-full" />
              <div className="border-b border-dashed border-white/20 w-full" />
              <div className="border-b border-dashed border-white/20 w-full" />
            </div>

            {/* Top Stat Overlay */}
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-[10px] font-mono uppercase text-slate-500">Total System Revenue</p>
                <p className="text-2xl font-black text-white font-mono">${money(stats.total_earnings)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase text-emerald-400">● Database Synced</p>
                <p className="text-xs font-mono text-slate-400">{appsCount} Real Apps Active</p>
              </div>
            </div>

            {/* Dynamic Curve SVG */}
            <div className="h-44 my-4 relative z-10 flex items-end">
              <svg className="w-full h-full overflow-visible" viewBox="0 0 500 120" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="chartGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,90 Q60,40 120,70 T240,30 T360,50 T480,20 L500,15 L500,120 L0,120 Z"
                  fill="url(#chartGlow)"
                />
                <path
                  d="M0,90 Q60,40 120,70 T240,30 T360,50 T480,20 L500,15"
                  fill="none"
                  stroke="#818cf8"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <circle cx="500" cy="15" r="5" fill="#818cf8" className="animate-ping" />
                <circle cx="500" cy="15" r="4" fill="#ffffff" />
              </svg>
            </div>

            {/* X-Axis Labels */}
            <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 relative z-10 pt-2 border-t border-white/5">
              <span>Yesterday (${money(stats.yesterday_earnings)})</span>
              <span>Today (${money(stats.today_earnings)})</span>
              <span>Monthly (${money(stats.this_month_earnings)})</span>
              <span>Total (${money(stats.total_earnings)})</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Database Live Sync</span>
            <Link to="/admin/earnings" className="text-indigo-400 hover:text-white font-bold flex items-center gap-1">
              Edit Revenue & Earnings Console <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* System Activity Log */}
        <div className="glass-card p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="admin-heading text-xl font-bold text-white">System Activity</h2>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            </div>

            <div className="space-y-4">
              {[
                { label: 'Revenue Telemetry', text: `Total Revenue: $${money(stats.total_earnings)}`, time: 'Synced', icon: DollarSign, color: 'text-emerald-400' },
                { label: 'Ecosystem Suite', text: `${appsCount} Applications Registered`, time: 'Active', icon: Smartphone, color: 'text-indigo-400' },
                { label: 'Monthly Delta', text: `Monthly Revenue: $${money(stats.this_month_earnings)}`, time: 'Live', icon: Activity, color: 'text-purple-400' },
                { label: 'Daily Earnings', text: `Today Earnings: $${money(stats.today_earnings)}`, time: 'Today', icon: TrendingUp, color: 'text-blue-400' },
                { label: 'Security & Auth', text: 'Master Administrator Authorized', time: 'Active', icon: ShieldCheck, color: 'text-amber-400' },
              ].map((log, i) => (
                <div key={i} className="flex items-center gap-3.5 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-indigo-500/20 transition-all">
                  <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${log.color}`}>
                    <log.icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-slate-400">{log.label}</span>
                      <span className="text-[9px] font-mono text-slate-500">{log.time}</span>
                    </div>
                    <p className="text-xs text-slate-200 font-medium truncate mt-0.5">{log.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-slate-400 font-mono">Quivix Admin Core</span>
            <Link to="/admin/earnings" className="text-xs font-bold text-indigo-400 hover:text-white flex items-center gap-1">
              Earnings Management →
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
